// Operator-facing connect flow for browser-automation integrations.
//
// Why: some third-party tools (Ahrefs Recrawl on Standard tier,
// Cloudflare Pages config toggles, etc.) only expose UI — no API.
// We drive those via Browserbase + Stagehand from RankForge, but the
// initial sign-in has to be done by the operator (MFA, SSO, password
// vault). This page is that handoff:
//
//   1. Operator clicks "Connect Ahrefs"
//   2. Backend creates a Browserbase persistent context + session
//   3. Dashboard opens the session's live-view URL in a new tab
//   4. Operator signs in via Ahrefs UI inside that cloud Chromium tab
//   5. Cookies persist back to the context
//   6. Operator clicks "I've signed in" → session closes, status flips
//      to 'connected'
//
// Future runs (Audrey post-deploy verification → "trigger recrawl")
// start a fresh session against the same context and inherit auth.
//
// One card per integration so the framework extends as we add more.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  integrationsApi,
  type AhrefsProject,
  type IntegrationStatus,
} from "../lib/api";

export function IntegrationsPage() {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Integrations</h1>
          <p className="text-ink-600 text-sm mt-1">
            One-time sign-in for tools that don't expose an API. RankForge
            drives the UI via Browserbase + Stagehand using the cookies you
            establish here. Re-connect if a run fails with{" "}
            <code className="bg-ink-100 px-1 rounded text-xs">session_expired</code>.
          </p>
        </div>
      </div>

      <AhrefsCard />
    </div>
  );
}

function AhrefsCard() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["integrations", "ahrefs", "status"],
    queryFn: () => integrationsApi.status("ahrefs"),
    refetchInterval: (q) => (q.state.data?.status === "pending" ? 5_000 : 30_000),
  });
  const projectsQ = useQuery({
    queryKey: ["integrations", "ahrefs", "projects"],
    queryFn: () => integrationsApi.ahrefsProjects(),
  });

  const status = statusQ.data?.status ?? "disconnected";
  const configured = statusQ.data?.configured ?? false;
  const mappedCount = statusQ.data?.mappedProjectCount ?? 0;
  const lastVerified = statusQ.data?.lastVerifiedAt;
  const lastUsed = statusQ.data?.lastUsedAt;
  const lastError = statusQ.data?.lastError;

  const [pendingPopup, setPendingPopup] = useState<{
    liveViewUrl: string;
    targetUrl: string | null;
  } | null>(null);

  const startMut = useMutation({
    mutationFn: () => integrationsApi.connectStart("ahrefs"),
    onSuccess: (data) => {
      // Open in a new tab — iframes won't work for Ahrefs (their
      // server sets X-Frame-Options: DENY).
      window.open(data.liveViewUrl, "_blank", "noopener");
      setPendingPopup({ liveViewUrl: data.liveViewUrl, targetUrl: data.targetUrl });
      qc.invalidateQueries({ queryKey: ["integrations", "ahrefs", "status"] });
    },
  });

  const confirmMut = useMutation({
    mutationFn: () => integrationsApi.connectConfirm("ahrefs"),
    onSuccess: () => {
      setPendingPopup(null);
      qc.invalidateQueries({ queryKey: ["integrations", "ahrefs", "status"] });
    },
  });

  const disconnectMut = useMutation({
    mutationFn: () => integrationsApi.disconnect("ahrefs"),
    onSuccess: () => {
      setPendingPopup(null);
      qc.invalidateQueries({ queryKey: ["integrations", "ahrefs", "status"] });
    },
  });

  if (statusQ.isLoading) {
    return <Loading />;
  }
  if (statusQ.error) {
    return <ErrorBox msg={(statusQ.error as Error).message} />;
  }

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-6 mb-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-display font-semibold">Ahrefs Site Audit</h2>
            <StatusPill status={status} />
          </div>
          <p className="text-ink-600 text-sm mt-1.5">
            Drives the <strong>Recrawl</strong> button after Audrey verifies a
            deploy. Standard-tier Ahrefs has no API endpoint for triggering
            a recrawl, so this is UI-driven.
          </p>
          <div className="mt-3 text-xs text-ink-500 space-y-0.5">
            <div>
              <strong>{mappedCount}</strong> sites mapped to Ahrefs projects
            </div>
            {lastVerified && (
              <div>Connected: <span className="text-ink-700">{fmtTimeAgo(lastVerified)}</span></div>
            )}
            {lastUsed && (
              <div>Last recrawl run: <span className="text-ink-700">{fmtTimeAgo(lastUsed)}</span></div>
            )}
            {lastError && (
              <div className="text-red-700 mt-1">{lastError}</div>
            )}
          </div>
        </div>
        <ConnectControls
          status={status}
          configured={configured}
          starting={startMut.isPending}
          confirming={confirmMut.isPending}
          disconnecting={disconnectMut.isPending}
          onStart={() => startMut.mutate()}
          onConfirm={() => confirmMut.mutate()}
          onDisconnect={() => disconnectMut.mutate()}
          onReconnect={() => startMut.mutate()}
        />
      </div>

      {!configured && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 mb-4">
          Browserbase isn't configured on the server. Set{" "}
          <code className="bg-amber-100 px-1 rounded text-xs">BROWSERBASE_API_KEY</code> +{" "}
          <code className="bg-amber-100 px-1 rounded text-xs">BROWSERBASE_PROJECT_ID</code> in{" "}
          <code className="bg-amber-100 px-1 rounded text-xs">.env</code> and restart the server.
        </div>
      )}

      {pendingPopup && status === "pending" && (
        <PendingConnectPanel
          liveViewUrl={pendingPopup.liveViewUrl}
          targetUrl={pendingPopup.targetUrl}
          onConfirm={() => confirmMut.mutate()}
          onCancel={() => disconnectMut.mutate()}
          confirming={confirmMut.isPending}
        />
      )}

      {status === "connected" && (
        <>
          <RecrawlPanel projects={projectsQ.data?.projects ?? []} />
          <ExcludeUrlsPanel projects={projectsQ.data?.projects ?? []} />
        </>
      )}

      {(startMut.error || confirmMut.error || disconnectMut.error) && (
        <ErrorBox
          msg={
            (startMut.error as Error | undefined)?.message ??
            (confirmMut.error as Error | undefined)?.message ??
            (disconnectMut.error as Error | undefined)?.message ??
            ""
          }
        />
      )}
    </div>
  );
}

function ConnectControls(props: {
  status: IntegrationStatus["status"];
  configured: boolean;
  starting: boolean;
  confirming: boolean;
  disconnecting: boolean;
  onStart: () => void;
  onConfirm: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
}) {
  const { status, configured, starting, disconnecting, onStart, onDisconnect, onReconnect } = props;
  const disabled = !configured;

  if (status === "disconnected") {
    return (
      <button
        type="button"
        disabled={disabled || starting}
        onClick={onStart}
        className="bg-ink-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50 hover:bg-ink-800"
      >
        {starting ? "Starting…" : "Connect Ahrefs"}
      </button>
    );
  }
  if (status === "pending") {
    return (
      <button
        type="button"
        disabled={disconnecting}
        onClick={onDisconnect}
        className="bg-white text-ink-700 text-sm font-medium px-4 py-2 rounded-md border border-ink-200 hover:bg-ink-50"
      >
        Cancel
      </button>
    );
  }
  if (status === "expired") {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || starting}
          onClick={onReconnect}
          className="bg-ink-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50 hover:bg-ink-800"
        >
          {starting ? "Reconnecting…" : "Reconnect"}
        </button>
      </div>
    );
  }
  // connected
  return (
    <button
      type="button"
      disabled={disconnecting}
      onClick={onDisconnect}
      className="text-ink-500 text-sm hover:text-ink-700 underline"
    >
      Disconnect
    </button>
  );
}

function PendingConnectPanel(props: {
  liveViewUrl: string;
  targetUrl: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirming: boolean;
}) {
  return (
    <div className="rounded-md bg-blue-50 border border-blue-200 p-4 space-y-3">
      <div className="text-sm text-blue-900 font-medium">
        Sign in to Ahrefs in the new tab we opened.
      </div>
      <ol className="text-sm text-blue-900 space-y-1 ml-4 list-decimal">
        <li>
          The new tab is a cloud Chromium browser running on Browserbase. Once
          you sign in there, RankForge inherits those cookies for future runs.
        </li>
        <li>
          {props.targetUrl ? (
            <>
              If the tab opened to a blank page, type{" "}
              <code className="bg-blue-100 px-1 rounded text-xs">{props.targetUrl}</code>{" "}
              into its address bar.
            </>
          ) : (
            "Navigate to the integration's sign-in page if needed."
          )}
        </li>
        <li>Complete the sign-in (including MFA if prompted).</li>
        <li>Come back here and click <strong>I've signed in</strong>.</li>
      </ol>
      <div className="flex gap-2 items-center pt-1">
        <a
          href={props.liveViewUrl}
          target="_blank"
          rel="noopener"
          className="text-xs text-blue-700 underline"
        >
          Reopen the cloud browser tab
        </a>
        <div className="flex-1" />
        <button
          type="button"
          onClick={props.onCancel}
          className="text-xs text-ink-500 hover:text-ink-700 underline"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={props.onConfirm}
          disabled={props.confirming}
          className="bg-blue-600 text-white text-sm font-medium px-3 py-1.5 rounded-md disabled:opacity-50 hover:bg-blue-700"
        >
          {props.confirming ? "Confirming…" : "I've signed in"}
        </button>
      </div>
    </div>
  );
}

function RecrawlPanel({ projects }: { projects: AhrefsProject[] }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [lastRun, setLastRun] = useState<{ dispatched: number; runsUrl: string } | null>(null);

  const validProjects = useMemo(
    () => projects.filter((p): p is AhrefsProject & { ahrefsProjectId: string } => Boolean(p.ahrefsProjectId)),
    [projects],
  );

  const recrawlMut = useMutation({
    mutationFn: (body: { all?: boolean; projectIds?: string[] }) => integrationsApi.ahrefsRecrawl(body),
    onSuccess: (data) => {
      setLastRun({ dispatched: data.dispatched, runsUrl: data.runsUrl });
      qc.invalidateQueries({ queryKey: ["integrations", "ahrefs", "status"] });
    },
  });

  function toggle(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  }

  return (
    <div className="border-t border-ink-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-800">Recrawl</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Dispatches a GitHub Actions workflow that drives Ahrefs's UI for
            each selected project. Each click takes ~10s. The recrawl itself
            runs server-side at Ahrefs — usually completes in 5–60 min.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => recrawlMut.mutate({ projectIds: Array.from(picked) })}
            disabled={picked.size === 0 || recrawlMut.isPending}
            className="bg-white border border-ink-200 text-ink-700 text-sm px-3 py-1.5 rounded-md hover:bg-ink-50 disabled:opacity-50"
          >
            Recrawl selected ({picked.size})
          </button>
          <button
            type="button"
            onClick={() => recrawlMut.mutate({ all: true })}
            disabled={validProjects.length === 0 || recrawlMut.isPending}
            className="bg-accent text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-accent-600 disabled:opacity-50"
          >
            {recrawlMut.isPending ? "Dispatching…" : `Recrawl all (${validProjects.length})`}
          </button>
        </div>
      </div>

      {lastRun && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 mb-3">
          Dispatched recrawl for {lastRun.dispatched} project{lastRun.dispatched === 1 ? "" : "s"}.{" "}
          <a className="underline" href={lastRun.runsUrl} target="_blank" rel="noopener">
            Watch the workflow run →
          </a>
        </div>
      )}

      {recrawlMut.error && (
        <ErrorBox msg={(recrawlMut.error as Error).message} />
      )}

      {validProjects.length === 0 ? (
        <div className="text-sm text-ink-500 p-3 bg-ink-50 rounded-md">
          No sites have an Ahrefs project mapped yet. Run{" "}
          <code className="bg-ink-100 px-1 rounded text-xs">rankforge audrey-ping</code>{" "}
          to discover and store project IDs.
        </div>
      ) : (
        <div className="border border-ink-100 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left">Domain</th>
                <th className="px-3 py-2 text-left">Ahrefs project ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {validProjects.map((p) => (
                <tr key={p.siteId}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={picked.has(p.ahrefsProjectId)}
                      onChange={() => toggle(p.ahrefsProjectId)}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{p.domain}</td>
                  <td className="px-3 py-2 font-mono text-xs text-ink-500">{p.ahrefsProjectId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// One-shot config push: add a URL exclusion pattern to every Ahrefs
// project so the Ahrefs UI's raw counts stop including Cloudflare
// email-protection URLs (/cdn-cgi/l/email-protection*). Doesn't change
// our real_count — Audrey already strips these at tick time — it just
// makes the Ahrefs dashboard agree with reality.
function ExcludeUrlsPanel({ projects }: { projects: AhrefsProject[] }) {
  const qc = useQueryClient();
  const validProjects = useMemo(
    () => projects.filter((p): p is AhrefsProject & { ahrefsProjectId: string } => Boolean(p.ahrefsProjectId)),
    [projects],
  );
  const [pattern, setPattern] = useState("/cdn-cgi/*");
  const [lastRun, setLastRun] = useState<{ dispatched: number; pattern: string; runsUrl: string } | null>(null);

  const mut = useMutation({
    mutationFn: (body: { all: boolean; pattern: string }) => integrationsApi.ahrefsExcludeUrls(body),
    onSuccess: (data) => {
      setLastRun({ dispatched: data.dispatched, pattern: data.pattern, runsUrl: data.runsUrl });
      qc.invalidateQueries({ queryKey: ["integrations", "ahrefs", "status"] });
    },
  });

  return (
    <div className="border-t border-ink-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-ink-800">Exclude URL pattern (one-time setup)</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Pushes a URL-exclusion pattern into every project's settings. Default
            <code className="bg-ink-100 px-1 rounded text-xs ml-1">/cdn-cgi/*</code>
            strips Cloudflare email-protection URLs that Ahrefs UI counts as 4xx
            errors — so Ahrefs's dashboard agrees with our real counts.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          placeholder="/cdn-cgi/*"
          className="border border-ink-200 rounded-md px-2 py-1.5 text-sm font-mono text-xs flex-1 max-w-xs"
        />
        <button
          type="button"
          onClick={() => mut.mutate({ all: true, pattern })}
          disabled={validProjects.length === 0 || mut.isPending || pattern.trim().length === 0}
          className="bg-ink-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-ink-800 disabled:opacity-50"
        >
          {mut.isPending ? "Dispatching…" : `Apply to all (${validProjects.length})`}
        </button>
      </div>

      {lastRun && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 mt-3">
          Dispatched exclusion <code className="bg-emerald-100 px-1 rounded text-xs">{lastRun.pattern}</code> to{" "}
          {lastRun.dispatched} project{lastRun.dispatched === 1 ? "" : "s"}.{" "}
          <a className="underline" href={lastRun.runsUrl} target="_blank" rel="noopener">
            Watch the workflow →
          </a>
        </div>
      )}

      {mut.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 mt-3 text-sm">
          Failed: {(mut.error as Error).message}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: IntegrationStatus["status"] }) {
  const styles: Record<IntegrationStatus["status"], string> = {
    connected: "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending: "bg-blue-100 text-blue-800 border-blue-200",
    expired: "bg-amber-100 text-amber-900 border-amber-200",
    disconnected: "bg-ink-100 text-ink-600 border-ink-200",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${styles[status]}`}>
      {status}
    </span>
  );
}

function fmtTimeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function Loading() {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">
      Loading…
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">Failed: {msg}</div>;
}

// Silence unused-react-state warning when not in dev tooling.
void useEffect;
